import React from "react";
import { Content, Header, FooterV2 } from "@/components/Modals";
import type {
  tTaskPayoutRequestModalProps,
  tTaskPayoutScreenQueryRes,
} from "./types";
import { useTranslation } from "react-i18next";
import { ORDERS_NS } from "@/i18n/ns";
import { useQuery } from "react-query";
import { QueryKeys } from "@/network/queryKeys";
import { QueriesAPI } from "@/api/queries";
import QueryRequestWithModal from "@/components/QueryRequest/withModal";
import { useMut } from "@/hooks/useMut";
import { TasksService } from "@/services/tasks";
import { eTaskSafedealProvider } from "@/types";
import Info from "_c/atoms/Info";
import { FORM_ID, ns } from "./config";
import TestPayoutRequest from "./Test";
import TinkoffIndividualsPayoutRequest from "./TinkoffIndividuals";
import { CurrencyFormatter } from "@/i18n/currency";

const TaskPayoutRequest = ({
  onClose,
  task,
  queryData,
}: Omit<tTaskPayoutRequestModalProps, "isOpen"> & {
  queryData: tTaskPayoutScreenQueryRes;
}) => {
  const { t, ready } = useTranslation(["common", "errors", ORDERS_NS], {
    useSuspense: false,
  });
  const requestPayoutMut = useMut(
    async (data: any) => {
      await TasksService.requestPayout(task, data);
    },
    {
      t,
      toast: () => t(ns + "request_success"),
      onSuccess: onClose,
      notifyOnError: true,
    }
  );
  if (!ready) {
    return null;
  }
  const renderForm = () => {
    if (queryData.safe_provider === eTaskSafedealProvider.TEST) {
      return <TestPayoutRequest onSubmit={requestPayoutMut.mutate} />;
    } else if (
      queryData.safe_provider === eTaskSafedealProvider.TINKOFF_INVIDIUALS
    ) {
      return (
        <TinkoffIndividualsPayoutRequest
          t={t}
          onSubmit={requestPayoutMut.mutate}
        />
      );
    } else {
      return <Info isError>{t(ns + "payout_unsupported")}</Info>;
    }
  };
  return (
    <>
      <Header onClick={onClose}>{t("common:close")}</Header>
      <Content>
        {renderForm()}
        <Info>
          {t(ns + "payout_amount", {
            amount: CurrencyFormatter.format(queryData.payout_amount),
          })}
        </Info>
      </Content>
      <FooterV2
        buttonType="submit"
        buttonForm={FORM_ID}
        disabled={requestPayoutMut.isLoading}
      >
        {t(ns + "make_request")}
      </FooterV2>
    </>
  );
};
const TaskPayoutRequestWithQuery = (
  props: Omit<tTaskPayoutRequestModalProps, "isOpen">
) => {
  const query = useQuery(
    [QueryKeys.taskPayoutScreen, props.task],
    () =>
      QueriesAPI.taskPayoutScreen({
        task: props.task,
      }) as Promise<tTaskPayoutScreenQueryRes>,
    {
      staleTime: 0,
      refetchInterval: false,
      cacheTime: 0,
    }
  );
  return (
    <QueryRequestWithModal onClose={props.onClose} query={query}>
      <TaskPayoutRequest
        task={props.task}
        onClose={props.onClose}
        queryData={query.data!}
      />
    </QueryRequestWithModal>
  );
};

export default TaskPayoutRequestWithQuery;
